import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../../../../context/LocaleContext';
import api from '../../../../api/axios';
import clsx from 'clsx';
import styles from '../../css/LandingHeader.module.css';
import {
  PRESS_HOVER,
  PRESS_TRANSITION,
  SUBTLE_PRESS_TAP,
} from '../../../../utils/motionInteractions';

function HeaderLanguageToggle({ isAuth = false }) {
  const { i18n } = useTranslation();
  const { setLanguage } = useLocale();

  const current = (i18n.language || 'en').toLowerCase();
  const isRu = current.startsWith('ru');
  const isSk = current.startsWith('sk');
  const isEn = !isRu && !isSk;

  // RU is forced by region rules — no toggle
  if (isRu) return null;

  const apply = async (lang) => {
    // switch UI immediately
    setLanguage(lang, { persist: true });

    // also persist on backend for authenticated users
    if (isAuth) {
      try {
        await api.post(
          '/accounts/set-language/',
          { language: lang },
          { meta: { suppressUnauthorizedRedirect: true, authOptional: true } }
        );
      } catch (_) {
        // ignore
      }
    }
  };

  return (
    <div className="flex items-center">
      <motion.button
        type="button"
        onClick={() => apply('en')}
        whileTap={SUBTLE_PRESS_TAP}
        transition={PRESS_TRANSITION}
        className={clsx(
          'px-1.5 py-1 text-white font-bold transition-opacity',
          isEn ? 'opacity-100' : 'opacity-45 hover:opacity-80'
        )}
      >
        EN
      </motion.button>
      <span className="inline-block w-3" />
      <motion.button
        type="button"
        onClick={() => apply('sk')}
        whileTap={SUBTLE_PRESS_TAP}
        transition={PRESS_TRANSITION}
        className={clsx(
          'px-1.5 py-1 text-white font-bold transition-opacity',
          isSk ? 'opacity-100' : 'opacity-45 hover:opacity-80'
        )}
      >
        SK
      </motion.button>
    </div>
  );
}

export function LandingHeaderFrame({ children, className, style }) {
  return (
    <div className={clsx(styles.root, className)} style={style}>
      {/* ─── Сам SVG-фон (rounded rect + blurred background) ─────────────────────────────── */}
      <svg
        className={styles.bgSvg}
        width="1410"
        height="90"
        viewBox="0 0 1410 90"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Внутри foreignObject ─ слой с backdrop-filter */}
        <foreignObject x="-10" y="-10" width="1430" height="110">
          <div
            xmlns="http://www.w3.org/1999/xhtml"
            className={styles.bgBlurLayer}
          />
        </foreignObject>
        <g data-figma-bg-blur-radius="10">
          {/* Заполнение (чуть прозрачный белый) */}
          <path
            d="M1365 0H45C20.1472 0 0 20.1472 0 45C0 69.8528 20.1472 90 45 90H1365C1389.85 90 1410 69.8528 1410 45C1410 20.1472 1389.85 0 1365 0Z"
            fill="white"
            fillOpacity="0.01"
          />
          {/* Обводка (тонкая белая с opacity) */}
          <path
            d="M45 0.5H1365C1389.58 0.5 1409.5 20.4233 1409.5 45C1409.5 69.5767 1389.58 89.5 1365 89.5H45C20.4233 89.5 0.5 69.5767 0.5 45C0.5 20.4233 20.4233 0.5 45 0.5Z"
            stroke="white"
            strokeOpacity="0.30"
          />
        </g>
        <defs>
          <clipPath
            id="bgblur_0_204_2662_clip_path"
            transform="translate(10 10)"
          >
            <path d="M1365 0H45C20.1472 0 0 20.1472 0 45C0 69.8528 20.1472 90 45 90H1365C1389.85 90 1410 69.8528 1410 45C1410 20.1472 1389.85 0 1365 0Z" />
          </clipPath>
        </defs>
      </svg>

      <div className={styles.content}>{children}</div>
    </div>
  );
}

const HERO_DESIGN_WIDTH = 1920;
const HERO_DESIGN_HEIGHT = 1023;

function LandingHeaderScaledFrame({ children }) {
  return (
    <LandingHeaderFrame className={styles.heroMatchedRoot}>
      {children}
    </LandingHeaderFrame>
  );
}

export function LandingHeaderHeroFrame({ children }) {
  const stageRef = React.useRef(null);

  React.useLayoutEffect(() => {
    const updateScale = () => {
      if (!stageRef.current) return;
      const isCompactHeader = window.matchMedia("(max-width: 1024px)").matches;

      if (isCompactHeader) {
        stageRef.current.style.transform = "translateX(-50%)";
        return;
      }

      const scale = Math.min(
        window.innerWidth / HERO_DESIGN_WIDTH,
        window.innerHeight / HERO_DESIGN_HEIGHT
      );
      stageRef.current.style.transform = `translateX(-50%) scale(${scale})`;
    };

    updateScale();
    window.addEventListener("resize", updateScale);
    return () => window.removeEventListener("resize", updateScale);
  }, []);

  return (
    <div ref={stageRef} className={styles.heroScaleStage}>
      <LandingHeaderFrame>{children}</LandingHeaderFrame>
    </div>
  );
}

export default function LandingHeader({
  isAuth,
  matchHeroScale = false,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation(); // defaultNS = "landing"
  const { t: tAbout } = useTranslation('about');
  const infoTabs = [
    ['about', 'tabs.about'],
    ['faq', 'tabs.faq'],
    ['rules', 'tabs.rules'],
    ['contacts', 'tabs.contacts'],
  ];

  const content = (
    <>
      <div className={styles.logoWrapper}>
        <img
          src="/assets/landing/castle-logo.svg"
          alt={t('header.logo.alt')}
          className={styles.logo}
        />
        <span className={styles.brandText}>
          {t('header.logo.brand')}
          <span className={styles.betaText}>{t('header.logo.beta_suffix')}</span>
        </span>
      </div>

      <nav className={styles.centerNav}>
        {infoTabs.map(([key, labelKey]) => (
          <motion.button
            key={key}
            onClick={() => navigate(`/about?tab=${key}`)}
            whileHover={PRESS_HOVER}
            whileTap={SUBTLE_PRESS_TAP}
            transition={PRESS_TRANSITION}
          >
            {tAbout(labelKey)}
          </motion.button>
        ))}
      </nav>

      <div className={styles.langWrapper}>
        <HeaderLanguageToggle isAuth={Boolean(isAuth)} />
      </div>
    </>
  );

  return matchHeroScale ? (
    <LandingHeaderScaledFrame>{content}</LandingHeaderScaledFrame>
  ) : (
    <LandingHeaderFrame>{content}</LandingHeaderFrame>
  );
}
